import React, {useState, useEffect, useRef} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    FormControl,
    Tooltip,
    Select,
    Spinner,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Menu,
    MenuButton,
    IconButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Checkbox,
} from "@chakra-ui/react";
import { cqAPI, getBid } from "../../lib/Auth";
import {EditIcon, InfoOutlineIcon} from "@chakra-ui/icons";
import CustomizeAIAssistantHeader from "./CustomizeAIAssistantHeader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faEllipsisV, faPlay } from "@fortawesome/free-solid-svg-icons";
import CustomizeAIDetail from "./CustomizeAIDetail";
import { rememberBookingFieldDefinitions } from "./bookingFieldDefinitions";
import moment from "moment/moment";
import {useNavigate} from "react-router-dom";
import CustomizeAIAssistantInstruction from "./CustomizeAIAssistantInstruction";
import { STORAGE_KEY_PREFIX } from "./CustomizeAIAssistant";

// Authored instruction sections. "General Instructions" maps to draft_response_pre_prompt
// (the `instructions` field); the rest are sibling section fields. `group` renders a shared
// heading for consecutive sections.
const INSTRUCTION_SECTIONS = [
  { key: 'booking_general_instruction', label: 'General Booking', group: 'Booking' },
  { key: 'booking_new_patient_instruction', label: 'New Patient Booking', group: 'Booking' },
  { key: 'booking_existing_patient_instruction', label: 'Existing Patient Booking', group: 'Booking' },
  { key: 'confirmation_instruction', label: 'Confirmation', group: null },
  { key: 'rescheduling_instruction', label: 'Rescheduling', group: null },
  { key: 'cancellation_instruction', label: 'Cancellation', group: null },
  { key: 'faq_instruction', label: 'FAQ', group: null },
  { key: 'routing_during_office_hours_instruction', label: 'During Office Hours', group: 'Routing Settings' },
  { key: 'routing_after_hours_instruction', label: 'After Hours', group: 'Routing Settings' },
];
const INSTRUCTION_SECTION_KEYS = INSTRUCTION_SECTIONS.map((s) => s.key);
const emptySections = () => Object.fromEntries(INSTRUCTION_SECTION_KEYS.map((k) => [k, '']));

const CustomizeAI = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const [instructions, setInstructions] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reorganize, setReorganize] = useState(false);
    const [organizedInstructions, setOrganizedInstructions] = useState({});
    const [updatedAt, setUpdatedAt] = useState("");
    const [bid, setBid] = useState("");
    const [vavGreeting, setVavGreeting] = useState('');
    const [vavLocationSelectorGreeting, setVavLocationSelectorGreeting] = useState('');
    const [selectedEzraVoice, setSelectedEzraVoice] = useState("ara");
    const [ezraVoices, setEzraVoices] = useState({});
    const [isPlayingEzraSample, setIsPlayingEzraSample] = useState(false);
    const [defaultGreeting, setDefaultGreeting] = useState('');
    const [isGreetingEditing, setIsGreetingEditing] = useState(false);
    const [isVavEnable, setIsVavEnable] = useState(false);
    const [isLocationSelectorEnable, setIsLocationSelectorEnable] = useState(false);
    const [isSavingGreeting, setIsSavingGreeting] = useState(false);
    const [defaultGreetingStart, setDefaultGreetingStart] = useState('');
    const [associatedBusinesses, setAssociatedBusinesses] = useState([]);
    const [disableSmsPostAppointment, setDisableSmsPostAppointment] = useState(false);
    const [sections, setSections] = useState(emptySections());
    const [sectionsDraft, setSectionsDraft] = useState(() => {
      try {
        const draft = localStorage.getItem(`${STORAGE_KEY_PREFIX + getBid()}-sections`);
        return draft ? { ...emptySections(), ...JSON.parse(draft) } : {};
      } catch {
        return {};
      }
    });
    const skipSectionsDraftPersistRef = useRef(false);
    const bgBoxColor = useColorModeValue("#ffffff", '#212330');
    const tabColor = useColorModeValue("blackAlpha", 'whiteAlpha');
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {isOpen: isSaveConfirmOpen, onOpen: onSaveConfirmOpen, onClose: onSaveConfirmClose} = useDisclosure()
    const toast = useToast();
    const navigate = useNavigate();

  const [instructionsDraft, setInstructionsDraft] = useState(() => {
    try {
      const draft = localStorage.getItem(STORAGE_KEY_PREFIX + getBid());
      return draft ? JSON.parse(draft) : "";
    } catch {
      return "";
    }
  });


  const fetchAiSettings = async () => {
    try {
      const response = await cqAPI.get("/settings/ai");
      if (response.type === "ok") {
        setDefaultGreetingStart(response?.data?.vav?.start || '');
        setDefaultGreeting(response?.data?.vav?.default_value || '');
        // SS-4258: the api's field list and allowed placements, read by CustomizeAIDetail.
        rememberBookingFieldDefinitions(response?.data?.booking_field_definitions);
        setOrganizedInstructions(response?.data?.organized_instructions || {});
        setInstructions(response?.data?.instructions || "");
        setSelectedEzraVoice(response?.data?.ezra_voice || "ara");
        setEzraVoices(response?.data?.ezra_voices || {
          'ara' : 'Warm and friendly Female', 
          'rex' : 'Confident and clear Male', 
          'sal' : 'Smooth and balanced Neutral', 
          'eve' : 'Energetic and upbeat Female', 
          'leo' : 'Authoritative and strong Male'
        });
        setUpdatedAt(response?.data?.updated || "");
        setBid(response?.data?.bid);
        if (response.data?.vav?.greeting) {
          setVavGreeting(response?.data?.vav?.greeting);
        } else {
          setVavGreeting(response?.data?.vav?.default_value || '')
        }
        if (response.data?.vav?.location_selector_greeting) {
          setVavLocationSelectorGreeting(response?.data?.vav?.location_selector_greeting);
        } else {
          setVavLocationSelectorGreeting(response?.data?.vav?.default_location_selector_greeting || '')
        }
        const fetchedSections = { ...emptySections(), ...(response?.data?.instruction_sections || {}) };
        setSections(fetchedSections);
        if (!Object.keys(sectionsDraft || {}).length) {
          setSectionsDraft(fetchedSections);
        }
        setAssociatedBusinesses(response?.data?.associated_businesses || []);
        setDisableSmsPostAppointment(!!response?.data?.disable_sms_post_appointment);
        setIsVavEnable(response.data.vav.enabled);
        setIsLocationSelectorEnable(response.data.vav.location_selector_enabled)
      }
    } catch (error) {
      console.error("Error loading voice auto pilot greeting:", error);
      toast({
        title: "Error",
        description: "Failed to load voice auto pilot greeting.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
    useEffect(() => {
        fetchAiSettings();
    }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + getBid(), JSON.stringify(instructionsDraft));
    } catch {
      console.error("Failed to save Drafts.");
    }
  }, [instructionsDraft]);

  useEffect(() => {
    if (skipSectionsDraftPersistRef.current) {
      skipSectionsDraftPersistRef.current = false;
      return;
    }
    try {
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX + getBid()}-sections`,
        JSON.stringify(sectionsDraft)
      );
    } catch {
      console.error("Failed to save section drafts.");
    }
  }, [sectionsDraft]);

    useEffect(() => {
        const draft = localStorage.getItem(STORAGE_KEY_PREFIX + getBid());
        draft && setInstructionsDraft(JSON.parse(draft));
    }, [])

    const handleSaveGreeting = async () => {
        try {
            setIsSavingGreeting(true);
            const response = await cqAPI.post("/settings/ai", {
                greeting: vavGreeting,
                location_selector_greeting: vavLocationSelectorGreeting,
                ezra_voice: selectedEzraVoice,
            });

            if (response.type === "ok") {
                toast({
                    title: response.msg || "Saved successfully.",
                    description: "Your voice auto pilot greeting have been saved.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setIsGreetingEditing(false);
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: error.msg,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
        setIsSavingGreeting(false);
    };

    const handleGreetingChange = async (value) => {
        setVavGreeting(value)
    }
    const handleLocationSelectorGreetingChange = async (value) => {
        setVavLocationSelectorGreeting(value)
    }

  const handlePlayEzraSample = async () => {
    if (isPlayingEzraSample) return;
    setIsPlayingEzraSample(true);
    let audioContext;
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await cqAPI.get(`/ezra/voice/sample`, { voice: selectedEzraVoice }, 15000);
      if (response.type !== 'ok' || !response.audio) throw new Error(response.msg || 'No audio returned');

      // Decode base64 WAV to ArrayBuffer
      const binaryString = atob(response.audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBuffer = await audioContext.decodeAudioData(bytes.buffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.onended = () => setIsPlayingEzraSample(false);
      source.start();
    } catch (error) {
      console.error('Error playing voice sample:', error);
      if (audioContext) audioContext.close();
      toast({
        title: "Error",
        description: "Failed to play voice sample.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsPlayingEzraSample(false);
    }
  };

  const handleSubmit = async () => {
    onSaveConfirmClose();
    try {
      setIsSubmitting(true);
      const res = await cqAPI.post("/settings/ai", {
        instructions: instructionsDraft,
        instruction_sections: sectionsDraft,
        reorganize,
      });
      if (res.type === "ok") {
        toast({
          title: "Updated!",
          description: "AI settings updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditing(false)
        setIsSubmitting(false);
        setReorganize(false);
        setInstructionsDraft("");
        skipSectionsDraftPersistRef.current = true;
        setSectionsDraft({});
        try {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX + getBid()}-sections`);
        } catch {
          console.error("Failed to clear section drafts.");
        }
        fetchAiSettings();
      } else {
        setIsSubmitting(false);
        toast({
          title: "Error",
          description: res.msg || "Failed to save.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (e) {
      setIsSubmitting(false);
      toast({
        title: "Error",
        description: e?.msg || "Unexpected error while configuring.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

    return (
      <Box m={4} px={4} width="100%" height="100%" overflowY="auto" backgroundColor={bgBoxColor} borderRadius="8px">
        <Tabs isFitted variant='line' width='full' colorScheme={tabColor} isLazy>
          <TabList width="75%">
            <Tab>Set Up AI</Tab>
            <Tab>Voice Auto Pilot Setup</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {/*<CustomizeAIAssistantHeader draft={instructionsDraft}/>*/}
              <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start" flexDirection="column" mt={2} alignItems='start'>
                      <Text fontSize="sm" fontWeight="bold" ml={2}>
                        AI Settings
                      </Text>
                      {updatedAt &&
                        <Text fontSize="sm" ml={2} mt={1}>
                          Updated: {moment(updatedAt).format("MMMM D, YYYY h:mm a")}
                        </Text>
                      }
                    </Flex>
                    <Flex justifyContent="flex-end" ml='auto' gap={2} my={1} mr={2} minHeight="40px"
                          alignItems="center">
                      {isEditing && (
                        <>
                          <Button
                            isDisabled={!isEditing || isSubmitting}
                            size="sm"
                            onClick={() => {
                              setIsEditing(false)
                              setInstructionsDraft("")
                              setSectionsDraft({})
                              skipSectionsDraftPersistRef.current = true;
                              try {
                                localStorage.removeItem(`${STORAGE_KEY_PREFIX + getBid()}-sections`);
                              } catch {
                                console.error("Failed to clear section drafts.");
                              }
                            }}>Cancel</Button>
                          <Button
                            isDisabled={!isEditing || isSubmitting}
                            size="sm"
                            onClick={onSaveConfirmOpen}> {isSubmitting ?
                            <Spinner/> : "Save"}
                          </Button>
                        </>)}
                      <Menu flexBasis="5%">
                        <MenuButton as={IconButton} aria-label="Options"
                                    icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                                    variant="ellipsis"/>
                        <MenuList>
                          {!isEditing &&
                            <MenuItem onClick={() => {
                              // Seed drafts from loaded values so sections the user doesn't
                              // touch are preserved on save (overlaying any restored draft).
                              setInstructionsDraft((prev) => prev || instructions);
                              setSectionsDraft((prev) => {
                                const seeded = { ...sections };
                                for (const k of INSTRUCTION_SECTION_KEYS) {
                                  if (prev[k] !== undefined && prev[k] !== '') seeded[k] = prev[k];
                                }
                                return seeded;
                              });
                              setIsEditing(true);
                            }}>
                              Edit
                            </MenuItem>
                          }
                          <MenuItem
                            onClick={() => navigate(`/settings/customize-ai/history?bid=${bid}`)}>
                            Setup History
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Flex>
                  <Flex minH="90px" flexDirection="column" gap={2} width="100%">
                    <Flex flexDirection="column" align="start" m={4}>
                      <Text fontSize="sm" fontWeight="bold" mb={2}>
                        General Instructions
                      </Text>
                      <Textarea
                        value={isEditing ? instructionsDraft || instructions : instructions}
                        isDisabled={!isEditing || isSubmitting}
                        height="400px"
                        onChange={(e) => {
                          setInstructionsDraft(e.target.value)
                        }}/>
                    </Flex>
                    {INSTRUCTION_SECTIONS.map((section, idx) => {
                      const showGroupHeader =
                        section.group && (idx === 0 || INSTRUCTION_SECTIONS[idx - 1].group !== section.group);
                      const draftVal = sectionsDraft[section.key];
                      const value = isEditing
                        ? (draftVal !== undefined ? draftVal : (sections[section.key] || ''))
                        : (sections[section.key] || '');
                      return (
                        <React.Fragment key={section.key}>
                          {showGroupHeader && (
                            <Text fontSize="md" fontWeight="bold" mt={4} mb={1} ml={4} alignSelf="flex-start" textAlign="start">
                              {section.group}
                            </Text>
                          )}
                          <Flex flexDirection="column" align="start" mx={4} mb={2} ml={section.group ? 8 : 4}>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>
                              {section.label}
                            </Text>
                            <Textarea
                              value={value}
                              isDisabled={!isEditing || isSubmitting}
                              height="150px"
                              placeholder={`Enter your ${section.label.toLowerCase()} instructions here`}
                              onChange={(e) => {
                                setSectionsDraft((prev) => ({ ...prev, [section.key]: e.target.value }))
                              }}/>
                          </Flex>
                        </React.Fragment>
                      );
                    })}
                  </Flex>
                </Flex>
              </Box>
              <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start" flexDirection="column" mt={2} alignItems='start'>
                      <Text fontSize="sm" fontWeight="bold" ml={2}>
                        AI Details
                      </Text>
                    </Flex>
                      <Flex justifyContent="flex-end" ml='auto' gap={2} my={1} mr={2} minHeight="40px"
                          alignItems="center">

                      <Menu flexBasis="5%">
                        <MenuButton as={IconButton} aria-label="Options"
                                    icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                                    variant="ellipsis"/>
                        <MenuList>
                          <MenuItem onClick={onOpen}>
                            Edit
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Flex>
                  <CustomizeAIDetail organized_instructions={organizedInstructions} associatedBusinesses={associatedBusinesses} disableSmsPostAppointment={disableSmsPostAppointment}/>
                </Flex>
              </Box>
            </TabPanel>
            <TabPanel>
              <Box p={1} bgColor={bgColor} borderRadius="md" width="full">
                <Flex align="center" minH="200px" flexDirection="column" alignItems="start">
                  <Flex flexDirection="row" alignItems="center" width="100%">
                    <Flex justifyContent="flex-start">
                      <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                        Voice Auto Pilot Greeting:
                      </Text>
                    </Flex>
                    <Flex justifyContent="flex-end" ml='auto' gap={2} mb={2} minHeight="40px"
                          alignItems="center">
                      {isVavEnable && isGreetingEditing ? (
                          <>
                            <Button id={"reset-greeting"}
                                    onClick={() => setVavGreeting(defaultGreeting)}
                                    size="sm" variant="ghost" mr={3} mt={1}>Reset to
                              Default</Button>
                            <Button isDisabled={isSavingGreeting} size="sm" id={"cancel-greeting"}
                                    onClick={() => setIsGreetingEditing(false)}>cancel</Button>
                            <Button isDisabled={isSavingGreeting} size="sm" id={"save-greeting"}
                                    onClick={handleSaveGreeting}> {isSavingGreeting ?
                              <Spinner/> : "Save"}
                            </Button>
                          </>)
                        :
                        isVavEnable && (
                          <EditIcon id={"edit-greeting"} mr={1}
                                    onClick={() => isVavEnable && setIsGreetingEditing(true)}/>)
                      }
                    </Flex>
                  </Flex>
                  {/* Greeting textarea */}
                  <Tooltip
                    label={isVavEnable ? "Select edit to make changes" : "You need to enable autopilot feature."}
                    aria-label="A tooltip"
                    isDisabled={isGreetingEditing}
                    placement="top-end"
                    mr={6}
                  >
                    <Textarea
                      isDisabled={!isGreetingEditing}
                      placeholder="Enter your voice auto pilot greeting here..."
                      id="vav_greeting"
                      minH="120px"
                      value={vavGreeting}
                      onChange={(e) => handleGreetingChange(e.target.value)}
                    />
                  </Tooltip>
                    {isVavEnable && isLocationSelectorEnable && (
                      <>
                        {/* Location Selector Greeting textarea */}
                        <Flex justifyContent="flex-start">
                          <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                            Voice Auto Pilot Location Selector Greeting:
                          </Text>
                        </Flex>

                        <Tooltip
                          label="Select edit to make changes"
                          aria-label="A tooltip"
                          isDisabled={isGreetingEditing}
                          placement="top-end"
                          mr={6}
                        >
                          <Textarea
                            isDisabled={!isGreetingEditing}
                            placeholder="Enter your voice auto pilot location selector greeting here..."
                            id="vav_greeting"
                            minH="120px"
                            value={vavLocationSelectorGreeting}
                            onChange={(e) =>
                              handleLocationSelectorGreetingChange(e.target.value)
                            }
                          />
                        </Tooltip>
                      </>
                    )}
                <Box mt={6} width="100%">
                  <Flex justifyContent="flex-start">
                    <Text fontSize="sm" fontWeight="bold" mb={2} ml={2}>
                Voice:
                    </Text>
                  </Flex>
                  <FormControl mt={4} width="100%" isDisabled={!isGreetingEditing}>
                    <Select
                      id="ezra_voice_select"
                      value={selectedEzraVoice}
                      onChange={(e) => setSelectedEzraVoice(e.target.value)}
                      placeholder="Choose an Ezra voice"
                    >
                      {Object.keys(ezraVoices).map((k) => (
                        <option key={k} value={k}>{ezraVoices[k]}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <Flex mt={2} ml={2} alignItems="center">
                    <Button
                      size="sm"
                      variant="ghost"
                      isLoading={isPlayingEzraSample}
                      loadingText="Playing..."
                      onClick={handlePlayEzraSample}
                      leftIcon={<FontAwesomeIcon icon={faPlay} />}
                    >
                      Play Sample
                    </Button>
                  </Flex>
                </Box>
                </Flex>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <Modal onClose={onClose} isOpen={isOpen} size="lg">
          <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Edit AI Settings</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody color="#B53D37" fontWeight="bold" minHeight="150px">
                        We highly recommend using the AI setup assistant to update your settings. Would you like to
                        continue?
                    </ModalBody>
                    <ModalFooter mr={4}>
                        <Button variant="ghost" onClick={onClose} mr={2}>Cancel</Button>
                        <Button
                            onClick={() => {
                                onClose()
                                navigate(`/settings/customize-ai/edit?bid=${bid}`);
                            }} minWidth="80px">Yes</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        <Modal onClose={() => { onSaveConfirmClose(); setReorganize(false); }} isOpen={isSaveConfirmOpen} size="lg">
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Update AI Instructions</ModalHeader>
            <ModalCloseButton/>
            <ModalBody>
              <Text mb={4}>
                Are you sure you want to update your AI Settings? This will affect your voice and messaging autopilot behavior.
              </Text>
              <Flex alignItems="center" gap={2}>
                <Checkbox
                  size="sm"
                  isChecked={reorganize}
                  onChange={(e) => setReorganize(e.target.checked)}
                >
                  <Text fontSize="sm">Reprocess Instructions</Text>
                </Checkbox>
                <Tooltip
                  label="Turn this on when making major changes to your instructions (e.g., adding new rules, changing how calls are handled). The AI will reanalyze your instructions to update its behavior. Leave this off for minor edits like fixing typos or rewording."
                  placement="right"
                >
                  <InfoOutlineIcon boxSize={3} color="gray.500" cursor="pointer" />
                </Tooltip>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => { onSaveConfirmClose(); setReorganize(false); }} mr={2}>Cancel</Button>
              <Button onClick={handleSubmit} minWidth="80px">Confirm</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </Box>
    )
};

export default CustomizeAI;
export { INSTRUCTION_SECTIONS };
